import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material'
import { SPACING } from '@common/theme/spacing'
import { AddRounded, East } from '@mui/icons-material'
import { Fragment } from 'react/jsx-runtime'
import CreateStudySubjectDialog from '@features/study_details/Subjects/components/CreateStudySubjectDialog'
import { queryClient } from '@common/config/api/queryClient'
import { useState } from 'react'
import { useGetSubjectsList } from '@features/subject/hooks/useGetSubjectsList'
import { useParams } from 'react-router-dom'
import { useSubjectReassignementMutation } from '@features/qc_task_details_view/hooks/useSubjectReassignementMutation'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { TimepointPermission } from '@auth/permissionsEnum'

const MOVE_ITEMS = ['Site information', 'Subject status', 'Subject eForm', 'Source exams', 'QC results', 'Read results']

const DUPLICATE_ITEMS = ['Site information', 'Subject status', 'Subject eForm', 'Source exams']

type DestinationSubject = {
  subject_id: string
  pseudo_id: string
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  width: 888,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 32px)',
  overflow: 'hidden',
}

export default function SubjectReassignModal({
  open,
  onClose,
  currentSubjectPseudoId,
}: {
  open: boolean
  onClose: () => void
  currentSubjectPseudoId?: string
}) {
  const [isSubjectDialogVisible, setIsSubjectDialogVisible] = useState(false)
  const [selectedSubject, setSelectedSubject] = useState<DestinationSubject | null>(null)
  const [reassignOption, setReassignOption] = useState<'move' | 'duplicate'>('duplicate')
  const { mutateAsync: subjectReassignement } = useSubjectReassignementMutation()
  const { hasPermission } = useCombinedPermissions()

  const { studyId, subjectId } = useParams<{
    studyId: string
    subjectId: string
  }>()

  const { data, isFetching } = useGetSubjectsList(
    ['SubjectsList', 'SubjectReassign'],
    {
      study_id: studyId!,
      limit: 200,
      order_by_field: 'pseudo_id',
      asc_order: true,
    },
    { enabled: open && !!studyId },
  )

  const subjects: DestinationSubject[] =
    data?.data?.subjects
      .filter(
        (subject) =>
          subject.subject_id !== subjectId &&
          subject.study_timepoints.every((timepoint) => timepoint.timepoint_submissions.length === 0),
      )
      .map(({ subject_id, pseudo_id }) => ({ subject_id, pseudo_id })) || []

  const handleReassign = async () => {
    if (!selectedSubject) return

    await subjectReassignement({
      path: {
        study_id: studyId!,
        subject_id: subjectId!,
      },
      body: {
        to_subject_id: selectedSubject.subject_id,
        is_duplicate: reassignOption === 'duplicate',
      },
    })
    queryClient.invalidateQueries({ queryKey: ['SubjectsList'] })
    onClose()
  }

  return (
    <Fragment>
      <Modal open={open}>
        <Paper sx={modalStyle}>
          <Stack padding={SPACING.spacingXxl} alignSelf="stretch" alignItems="flex-start" flexShrink={0}>
            <Typography variant="h5" color="text.primary">
              Reassign subject data?
            </Typography>
          </Stack>
          <Stack
            paddingX={SPACING.spacingXxl}
            paddingTop={2}
            alignItems="center"
            alignSelf="stretch"
            flex={1}
            minHeight={0}
            overflow="auto"
          >
            <Stack direction="row" alignItems="center" alignSelf="stretch">
              <TextField fullWidth value={currentSubjectPseudoId || ''} disabled label="Current subject" size="small" />
              <Stack padding={1}>
                <East />
              </Stack>
              <Autocomplete
                fullWidth
                size="small"
                options={subjects}
                loading={isFetching}
                value={selectedSubject}
                onChange={(_, value) => setSelectedSubject(value)}
                getOptionLabel={(option) => option.pseudo_id}
                isOptionEqualToValue={(option, value) => option.subject_id === value.subject_id}
                noOptionsText="No empty destination subjects found"
                renderInput={(params) => (
                  <TextField {...params} label="Select a subject" placeholder="Search subject" />
                )}
              />
            </Stack>
            <Stack alignItems="center" alignSelf="stretch">
              <Button
                variant="text"
                color="secondary"
                startIcon={<AddRounded />}
                sx={{ marginLeft: 22 }}
                onClick={() => setIsSubjectDialogVisible(true)}
              >
                <Typography variant="body2" color="text.primary">
                  Add new subject
                </Typography>
              </Button>
            </Stack>

            <FormControl fullWidth>
              <RadioGroup
                value={reassignOption}
                onChange={(e) => setReassignOption(e.target.value as 'move' | 'duplicate')}
              >
                <Stack alignItems="flex-start" alignSelf="stretch" spacing={1}>
                  <Paper
                    sx={{
                      width: '100%',
                      borderRadius: 3,
                      padding: SPACING.spacingXl,
                      border: '1px solid',
                      borderColor: reassignOption === 'move' ? '#006B8C' : 'divider',
                    }}
                  >
                    <FormControlLabel
                      value="move"
                      control={<Radio />}
                      disabled={!hasPermission(TimepointPermission.ALL)}
                      label={
                        <Stack>
                          <Typography variant="body1" color="text.primary">
                            Completely reassign all data to a new subject
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Including existing QC and Read tasks and their results. The original subject will no longer
                            retain this data.
                          </Typography>
                          <List>
                            {MOVE_ITEMS.map((item) => (
                              <ListItem key={item} disablePadding>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" color="text.primary">
                                      {`• ${item}`}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Stack>
                      }
                    />
                  </Paper>
                  <Paper
                    sx={{
                      width: '100%',
                      borderRadius: 3,
                      padding: SPACING.spacingXl,
                      border: '1px solid',
                      borderColor: reassignOption === 'duplicate' ? '#006B8C' : 'divider',
                    }}
                  >
                    <FormControlLabel
                      value="duplicate"
                      control={<Radio />}
                      label={
                        <Stack>
                          <Typography variant="body1" color="text.primary">
                            Duplicate main data to a new subject
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Except existing QC and Read tasks and their results. The original subject’s data will remain
                            unchanged.
                          </Typography>
                          <List>
                            {DUPLICATE_ITEMS.map((item) => (
                              <ListItem key={item} disablePadding>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" color="text.primary">
                                      {`• ${item}`}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Stack>
                      }
                    />
                  </Paper>
                </Stack>
              </RadioGroup>
            </FormControl>
          </Stack>
          <Stack
            justifyContent="flex-end"
            direction="row"
            alignSelf="stretch"
            alignItems="center"
            padding={SPACING.spacingXxl}
            spacing={SPACING.spacingMd}
            flexShrink={0}
          >
            <Button
              variant="contained"
              sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
              disabled={!selectedSubject}
              onClick={handleReassign}
            >
              Reassign data
            </Button>
          </Stack>
        </Paper>
      </Modal>

      {isSubjectDialogVisible ? (
        <CreateStudySubjectDialog
          onClose={(created) => {
            setIsSubjectDialogVisible(false)
            if (created && 'id' in created) {
              setSelectedSubject({
                subject_id: created.id,
                pseudo_id: created.pseudo_id,
              })
              queryClient.invalidateQueries({ queryKey: ['SubjectsList'] })
            }
          }}
        />
      ) : null}
    </Fragment>
  )
}
